import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Loader2, ArrowRight } from 'lucide-react';

export const EmailCaptureScreen = ({ onSubmit, submitting, error }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const nameValid = firstName.trim().length > 0;
  const valid = emailValid && nameValid;

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    onSubmit({ firstName: firstName.trim(), email: email.trim().toLowerCase() });
  };

  return (
    <motion.section
      className="relative min-h-screen w-full bg-brand-bg flex items-center justify-center px-5 sm:px-6 pt-24 sm:pt-28 pb-12 sm:pb-16 overflow-hidden"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid="email-capture-screen"
    >
      {/* brand bar */}
      <div className="absolute top-0 left-0 right-0 px-5 sm:px-6 md:px-10 pt-5 sm:pt-7 max-w-7xl mx-auto">
        <img src="/brand/young-again-logo.png" alt="Young Again" className="h-8 sm:h-9 w-auto opacity-90" />
      </div>

      <div
        className="pointer-events-none absolute top-10 right-10 h-56 sm:h-72 w-56 sm:w-72 rounded-full blur-3xl opacity-50"
        style={{ background: 'radial-gradient(circle at center, #D8E5C0 0%, rgba(216,229,192,0) 70%)' }}
      />

      <div className="relative w-full max-w-xl">
        <div className="bg-white rounded-3xl shadow-xl shadow-brand-forest/10 border border-brand-border p-6 sm:p-8 md:p-12">
          <div className="flex items-center gap-3">
            <div className="h-px w-8 bg-brand-moss" />
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-moss font-heading">
              Analysis Complete
            </span>
          </div>

          <h2 className="mt-5 font-heading font-bold text-2xl sm:text-4xl lg:text-5xl text-brand-forest leading-[1.1] sm:leading-[1.05] tracking-tight" data-testid="email-capture-headline">
            Your personalized results are ready.
          </h2>

          <p className="mt-4 text-base sm:text-lg text-brand-muted font-body leading-relaxed">
            Enter your best email to see what your answers may reveal about your energy, aging, and cellular health — along with a special offer.
          </p>

          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-4" data-testid="email-capture-form">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-brand-muted mb-2 font-heading">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your first name"
                className="w-full bg-brand-bg border border-brand-border rounded-xl px-4 py-4 text-lg text-brand-forest focus:outline-none focus:ring-2 focus:ring-brand-forest focus:border-transparent transition-all placeholder:text-brand-muted/60 font-body"
                data-testid="first-name-input"
                autoComplete="given-name"
              />
              {touched && !nameValid && (
                <p className="mt-2 text-sm text-red-600 font-body">Please enter your first name.</p>
              )}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-brand-muted mb-2 font-heading">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-brand-bg border border-brand-border rounded-xl px-4 py-4 text-lg text-brand-forest focus:outline-none focus:ring-2 focus:ring-brand-forest focus:border-transparent transition-all placeholder:text-brand-muted/60 font-body"
                data-testid="email-input"
                autoComplete="email"
              />
              {touched && !emailValid && (
                <p className="mt-2 text-sm text-red-600 font-body">Please enter a valid email.</p>
              )}
            </div>

            {error && (
              <p className="text-sm text-red-600 font-body" data-testid="email-capture-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 w-full group inline-flex items-center justify-center gap-3 bg-brand-forest text-white hover:bg-brand-forestDark disabled:opacity-60 disabled:cursor-not-allowed transition-all rounded-xl px-8 py-4 text-base sm:text-lg font-heading font-semibold uppercase tracking-wider shadow-sm hover:shadow-lg"
              data-testid="show-results-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Preparing your results…
                </>
              ) : (
                <>
                  Show My Results
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 flex items-center gap-2 text-xs text-brand-muted font-body">
            <Lock className="w-3.5 h-3.5" />
            <span>Your information is private. No spam, unsubscribe anytime.</span>
          </div>
        </div>
      </div>
    </motion.section>
  );
};
